import { API_BASE_URL } from '../config';

export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available and required (default: true)
  const requireAuth = options.requireAuth !== false;
  const token = localStorage.getItem('auth_token');
  if (token && requireAuth) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const finalOptions = {
    method: 'GET', // Default to GET
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    ...options,
  };

  // Add timeout for AI endpoints (longer timeout for template generation)
  if (endpoint.includes('/ai/') && finalOptions.method === 'POST') {
    finalOptions.signal = AbortSignal.timeout(300000); // 5 minutes for AI operations (matches Lambda timeout)
  } else if (endpoint.includes('/templates') && finalOptions.method === 'POST') {
    finalOptions.signal = AbortSignal.timeout(60000); // 1 minute for template creation
  } else {
    finalOptions.signal = AbortSignal.timeout(10000); // 10 seconds for other operations
  }

  try {
    console.log(`Making ${finalOptions.method} request to:`, url);
    console.log('🔧 Request details:', {
      url,
      method: finalOptions.method,
      isMobile: !!(window.Capacitor || window.cordova),
      userAgent: navigator.userAgent?.substring(0, 100) || 'unknown'
    });
    const response = await fetch(url, finalOptions);
    
    if (!response.ok) {
      console.error(`Request failed with status ${response.status}:`, response.statusText);
      const errorData = await response.json().catch(() => ({}));
      
      // Create enhanced error object for timeout cases
      const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.suggestion = errorData.suggestion;
      error.estimatedTime = errorData.estimated_time;
      
      throw error;
    }
    
    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      status: error.status
    });
    
    // Handle specific error types
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      const enhancedError = new Error('Request timed out. The operation is taking longer than expected. Please try again with async mode.');
      enhancedError.isTimeoutError = true;
      enhancedError.suggestion = 'retry_async';
      enhancedError.estimatedTime = '30-60 seconds';
      throw enhancedError;
    }
    
    // Handle network timeouts and CORS specifically
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      // This could be CORS or network timeout
      const enhancedError = new Error('Network error: Please check your connection and try again. If the problem persists, the request may be taking too long.');
      enhancedError.isNetworkError = true;
      throw enhancedError;
    }
    
    // Handle empty error objects (common with CORS issues)
    if (!error.message || Object.keys(error).length === 0) {
      const enhancedError = new Error('Network connection failed. This may be due to CORS restrictions or network issues.');
      enhancedError.isNetworkError = true;
      enhancedError.originalError = error;
      throw enhancedError;
    }
    
    throw error;
  }
};